import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Type } from '@spotify-internal/encore-creator-web';

import styles from './index.module.scss';
import {
  selectNetworkDemographicsStatus,
  selectNetworkDemographics,
  selectDateRange,
  LOADING,
  ERROR,
} from '../../../store/selectors';
import { loadNetworkDemographics } from '../../../store/networkDemographics';

import { AgeDemographicsChart } from '../../../components/Shared/Charts/AgeDemographicsChart';
import { GenderDemographicsChart } from '../../../components/Shared/Charts/GenderDemographicsChart';
import { CountryListTable } from '../../../components/Shared/Tables/CountryListTable';
import { LoadingIndicator } from '../../../components/Shared/LoadingIndicator';
import { NetworkAudienceDemographics, DateRangeWithLabel, ReduxState } from '../../../types';
import { FormattedMessage } from 'react-intl';

type Props = {
  networkId: string;
  status?: 'LOADING' | 'ERROR';
  networkDemographics?: NetworkAudienceDemographics;
  loadNetworkDemographics: typeof loadNetworkDemographics;
  dateRange?: DateRangeWithLabel;
};

export class NetworkDemographics extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadNetworkDemographics(this.props.networkId);
  }

  /* istanbul ignore next */
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!this.props.dateRange || !nextProps.dateRange) {
      return;
    }

    if (
      nextProps.dateRange.startDate !== this.props.dateRange.startDate ||
      nextProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.props.loadNetworkDemographics(this.props.networkId);
    }
  }

  renderLoader = () => (
    <div style={{ width: '100%', height: '350px' }}>
      <LoadingIndicator />
    </div>
  );

  render() {
    switch (this.props.status) {
      case ERROR:
        return <span data-qa="network-demographics-error" />;
      default:
        break;
    }

    if (!this.props.networkDemographics && this.props.status !== LOADING) return null;

    const {
      genderDemographicsByAgeGroup: ageData = [],
      percentageByGender: genderData = [],
      countryDemographics: countryData = [],
    } = this.props.networkDemographics || {};

    return (
      <div data-qa="network-demographics-charts">
        <div className={styles.chart}>
          <Type as="h1" variant={Type.heading3}>
            <FormattedMessage
              id="network.audience.gender.title"
              description="Title of gender chart"
              defaultMessage="Gender"
            />
          </Type>
          {this.props.status === LOADING ? (
            this.renderLoader()
          ) : (
            <GenderDemographicsChart genderData={genderData} />
          )}
        </div>
        <div className={styles.chart}>
          <Type as="h1" variant={Type.heading3}>
            <FormattedMessage
              id="network.audience.age.title"
              description="Title of age chart"
              defaultMessage="Age"
            />
          </Type>
          {this.props.status === LOADING ? (
            this.renderLoader()
          ) : (
            <AgeDemographicsChart ageData={ageData} />
          )}
        </div>
        <div className={styles.chart}>
          <Type as="h1" variant={Type.heading3}>
            <FormattedMessage
              id="network.audience.country.title"
              description="Title of country chart"
              defaultMessage="Country"
            />
          </Type>
          <CountryListTable countryData={countryData} />
        </div>
      </div>
    );
  }
}

const selectors = createStructuredSelector<
  ReduxState,
  {
    status: ReturnType<typeof selectNetworkDemographicsStatus>;
    networkDemographics: ReturnType<typeof selectNetworkDemographics>;
    dateRange: ReturnType<typeof selectDateRange>;
  }
>({
  status: selectNetworkDemographicsStatus,
  networkDemographics: selectNetworkDemographics,
  dateRange: selectDateRange,
});

const actions = {
  loadNetworkDemographics,
};

export const ConnectedNetworkDemographics = connect(selectors, actions)(NetworkDemographics);
